import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faUsers, 
  faChartLine,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';

const CommunityInsightModal = ({ isOpen, onClose, communityStats, theme }) => {
  if (!communityStats) return null;

  const getIcon = () => {
    if (communityStats.level === 'easy') return faCheckCircle;
    if (communityStats.level === 'medium') return faExclamationTriangle;
    return faTimesCircle;
  };

  const getGradient = () => {
    if (communityStats.level === 'easy') {
      return `linear-gradient(135deg, ${theme?.dashboard?.status?.correct}20, ${theme?.dashboard?.status?.correct}40)`;
    } else if (communityStats.level === 'medium') {
      return `linear-gradient(135deg, ${theme?.dashboard?.status?.warning}20, ${theme?.dashboard?.status?.warning}40)`;
    }
    return `linear-gradient(135deg, ${theme?.dashboard?.status?.incorrect}20, ${theme?.dashboard?.status?.incorrect}40)`;
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2.5rem',
      maxWidth: '500px',
      width: '100%',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: getGradient(),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: communityStats.color,
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      marginBottom: '2rem',
      lineHeight: 1.6,
    },
    statsContainer: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '1rem',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    },
    statRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    statLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
    },
    statValue: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary,
    },
    progressBar: {
      width: '100%',
      height: '0.75rem',
      background: theme?.backgrounds?.tertiary,
      borderRadius: '0.375rem',
      overflow: 'hidden',
      marginTop: '0.5rem',
    },
    progressFill: {
      height: '100%',
      background: communityStats.color,
      transition: 'width 1s ease',
    },
    message: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '0.75rem',
      padding: '1rem',
      marginTop: '1rem',
      textAlign: 'center',
      fontSize: '0.875rem',
      color: communityStats.color,
      fontWeight: '500',
      border: `1px solid ${communityStats.color}30`,
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.closeButton}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <motion.div
              style={styles.iconContainer}
              animate={{ 
                rotate: [0, -5, 5, 0],
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1,
              }}
            >
              <FontAwesomeIcon icon={getIcon()} style={styles.icon} />
            </motion.div>
            
            <h2 style={styles.title}>Community Insights</h2>
            <p style={styles.subtitle}>
              See how other learners performed on this question
            </p>
            
            {/* <div style={styles.statsContainer}>
              <div style={styles.statRow}>
                <div style={styles.statLabel}>
                  <FontAwesomeIcon icon={faUsers} />
                  <span>Success Rate</span>
                </div>
                <div style={styles.statValue}>
                  {Math.round(communityStats.percentage)}%
                </div>
              </div>
              
              <div style={styles.progressBar}>
                <motion.div
                  style={{ ...styles.progressFill, width: `${communityStats.percentage}%` }}
                  initial={{ width: 0 }}
                  animate={{ width: `${communityStats.percentage}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
            </div> */}
            
            <div style={styles.message}>
              {communityStats.message}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommunityInsightModal;